import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { useTranslation } from '../t';
import { userStore } from '../stores/UserStore';
import http from '../http';
import ICP from '../components/ICP';
import { passwordRegex, phoneRegex } from '../const';
import wechat_logo from '../assets/wechat_logo.svg';
import logo from '../assets/logo.png';

const C = {
    brand: '#F777B0',
    ink: '#1A1614',
    ink2: '#3D3632',
    ink3: '#8A8079',
    paper: '#EDE6D5',
    paper2: '#E2D9C6',
    hair: '#E8E0D0',
    hair2: '#D4CABA',
    serif: '"Instrument Serif", "Songti SC", serif',
    sans: '"Inter", "Noto Sans SC", -apple-system, sans-serif',
};

function Field({ label, type = 'text', name, value, onChange, error, placeholder, autoFocus }) {
    const [focused, setFocused] = useState(false);
    return (
        <div style={{ marginBottom: 20 }}>
            <label htmlFor={name} style={{
                display: 'block', fontSize: 11, fontWeight: 600,
                letterSpacing: 1.2, textTransform: 'uppercase',
                color: error ? '#c2412e' : focused ? C.brand : C.ink3,
                marginBottom: 8, transition: 'color .2s',
            }}>{label}</label>
            <input
                type={type}
                id={name}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                autoFocus={autoFocus}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '13px 16px',
                    fontFamily: C.sans, fontSize: 15, color: C.ink,
                    background: '#fff',
                    border: `1.5px solid ${error ? '#e88c7c' : focused ? C.brand : C.hair}`,
                    borderRadius: 10, outline: 'none',
                    transition: 'border-color .2s',
                }}
            />
            {error && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#c2412e' }}>{error}</div>
            )}
        </div>
    );
}

export const UserLoginPage = observer(({ snackbarStore }) => {
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [bannedAccount, setBannedAccount] = useState(null);
    const [wxLogin, setWxLogin] = useState(false);
    const [wxLoginStatus, setWxLoginStatus] = useState('pending');
    const [wxLoginQrCode, setWxLoginQrCode] = useState('');
    const [wxLoginError, setWxLoginError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const intervalId = useRef(null);
    const wxSessionId = useRef(null);
    const consumingWxSession = useRef(false);

    const validatePhone = v => {
        if (!v) return t('phone num can not be empty');
        if (!phoneRegex.test(v)) return t('_Phone Rule');
        return '';
    };
    const validatePassword = v => {
        if (process.env.NODE_ENV === 'development') return '';
        if (!v) return t('password can not be empty');
        if (!passwordRegex.test(v)) return t('_Password Rule');
        return '';
    };

    const handleChange = e => {
        const { name, value } = e.target;
        setBannedAccount(null);
        if (name === 'phone') { setPhone(value); setPhoneError(validatePhone(value)); }
        if (name === 'password') { setPassword(value); setPasswordError(validatePassword(value)); }
    };

    const handleSubmit = async e => {
        e.preventDefault();
        const pe = validatePhone(phone);
        const pw = validatePassword(password);
        setPhoneError(pe); setPasswordError(pw);
        setBannedAccount(null);
        if (pe || pw) return;
        setLoading(true);
        try {
            const res = await http.post('/login', { phone, password });
            userStore.login(res.data.token);
            snackbarStore.showSnackbar(t('Login successfully'));
            navigate('/user/orders');
        } catch (err) {
            const data = err?.response?.data;
            if (data?.code === 'USER_BANNED') {
                setBannedAccount({ reason: String(data.reason || '').trim() });
                setPassword('');
            } else {
                const message = data?.message;
                message && snackbarStore.showSnackbar(t(message), 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleWxLogin = async () => {
        clearInterval(intervalId.current);
        setWxLoginError('');
        setWxLoginStatus('pending');
        try {
            const res = await http.post('/wechat/qr-sessions/login');
            const { imageData, sessionId } = res.data;
            wxSessionId.current = sessionId;
            consumingWxSession.current = false;
            setWxLogin(true);
            setWxLoginQrCode(imageData);
            intervalId.current = setInterval(async () => {
                try {
                    const r = await http.get(`/wechat/qr-sessions/${sessionId}`);
                    const status = r?.data?.status;
                    if (!status) return;
                    setWxLoginStatus(status);
                    if (status === 'confirmed' && !consumingWxSession.current) {
                        consumingWxSession.current = true;
                        clearInterval(intervalId.current);
                        const consumed = await http.post(`/wechat/qr-sessions/${sessionId}/consume`);
                        wxSessionId.current = null;
                        userStore.login(consumed.data.token);
                        snackbarStore.showSnackbar(t('Login successfully'));
                        navigate('/user/orders');
                    } else if (status === 'consumed') {
                        clearInterval(intervalId.current);
                        wxSessionId.current = null;
                        consumingWxSession.current = false;
                        setWxLoginQrCode('');
                        setWxLoginError('二维码已使用，请重新生成');
                    } else if (status === 'conflict' || status === 'failed') {
                        clearInterval(intervalId.current);
                        setWxLoginQrCode('');
                        setWxLoginError(r.data.errorCode === 'WECHAT_NOT_BOUND'
                            ? '此微信尚未绑定摄影师账号'
                            : r.data.errorCode === 'WECHAT_ACCOUNT_CONFLICT'
                                ? '该微信绑定了多个账号，请联系平台处理'
                                : '微信确认失败，请重新生成二维码');
                    }
                } catch (error) {
                    clearInterval(intervalId.current);
                    wxSessionId.current = null;
                    consumingWxSession.current = false;
                    const consumed = error?.response?.data?.code === 'QR_SESSION_CONSUMED';
                    setWxLoginStatus(consumed ? 'consumed' : 'expired');
                    setWxLoginQrCode('');
                    setWxLoginError(consumed ? '二维码已使用，请重新生成' : '二维码已失效，请重新生成');
                }
            }, 1000);
        } catch (error) {
            setWxLogin(true);
            setWxLoginQrCode('');
            setWxLoginStatus('error');
            setWxLoginError('二维码生成失败，请稍后重试');
        }
    };

    const goBack = () => {
        setWxLogin(false);
        clearInterval(intervalId.current);
        wxSessionId.current = null;
        consumingWxSession.current = false;
        setWxLoginError('');
        setWxLoginStatus('pending');
    };

    useEffect(() => () => clearInterval(intervalId.current), []);

    return (
        <div style={{
            minHeight: '100vh', display: 'flex',
            fontFamily: C.sans,
            WebkitFontSmoothing: 'antialiased',
            background: C.paper,
        }}>
            {/* ── Left decorative panel ── */}
            <div style={{
                display: 'none',
                width: '42%', flexShrink: 0,
                background: C.ink,
                position: 'relative', overflow: 'hidden',
                flexDirection: 'column', justifyContent: 'space-between',
                padding: '56px 48px',
            }} className="login-panel">
                {/* diagonal watermark */}
                <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'rgba(255,255,255,.04)', fontSize: 120, fontWeight: 700,
                    letterSpacing: 16, transform: 'rotate(-20deg)',
                    fontFamily: C.serif, userSelect: 'none', pointerEvents: 'none',
                    lineHeight: 1,
                }}>图派图派图派图派图派</div>

                {/* top brand */}
                <div>
                    <div style={{ fontSize: 11, letterSpacing: 2, color: C.brand, fontWeight: 600, textTransform: 'uppercase' }}>
                        PICSECT
                    </div>
                </div>

                {/* center copy */}
                <div>
                    <p style={{
                        fontFamily: C.serif, fontWeight: 400,
                        fontSize: 42, lineHeight: 1.15, margin: '0 0 20px',
                        color: '#fff', letterSpacing: -0.5,
                    }}>
                        为每张照片，<br />找到最好的<br />
                        <span style={{ color: C.brand, fontStyle: 'italic' }}>归宿。</span>
                    </p>
                    <p style={{ fontSize: 13, color: 'rgba(255,255,255,.45)', lineHeight: 1.7, margin: 0, maxWidth: 280 }}>
                        为独立摄影师和摄影工作室打造的线上选片平台。简洁、专业，让客户选片成为一种享受。
                    </p>
                </div>

                {/* bottom accent bar */}
                <div style={{ display: 'flex', gap: 6 }}>
                    {[C.brand, '#D94F92', C.ink3, C.hair2].map((c, i) => (
                        <div key={i} style={{ height: 3, flex: 1, background: c, borderRadius: 99, opacity: i > 1 ? 0.4 : 1 }} />
                    ))}
                </div>
            </div>

            {/* ── Right form panel ── */}
            <div style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                padding: '48px 24px',
                background: C.paper,
            }}>
                <div style={{ width: '100%', maxWidth: 400 }}>
                    {/* brand header */}
                    <div style={{ marginBottom: 40, textAlign: 'center' }}>
                        <img src={logo} alt="图派" style={{ width: 104, height: 104, objectFit: 'contain', display: 'block', margin: '0 auto 10px' }}/>
                        <div style={{ fontSize: 11, letterSpacing: 2, color: C.brand, fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>
                            PICSECT
                        </div>
                        <h1 style={{
                            fontFamily: C.serif, fontWeight: 400,
                            fontSize: 38, lineHeight: 1.05, margin: 0,
                            color: C.ink, letterSpacing: -0.5,
                        }}>
                            {wxLogin ? '微信扫码登录' : '欢迎回来'}
                        </h1>
                        {!wxLogin && (
                            <p style={{ margin: '10px 0 0', fontSize: 14, color: C.ink3, lineHeight: 1.5 }}>
                                登录你的图派账号，管理你的选片项目。
                            </p>
                        )}
                    </div>

                    {/* ── Phone/password form ── */}
                    {!wxLogin && (
                        <form onSubmit={handleSubmit}>
                            <Field
                                label={t('phone')}
                                name="phone"
                                value={phone}
                                onChange={handleChange}
                                error={phoneError}
                                autoFocus
                            />
                            <Field
                                label={t('Password')}
                                type="password"
                                name="password"
                                value={password}
                                onChange={handleChange}
                                error={passwordError}
                            />

                            {bannedAccount && (
                                <div
                                    data-testid="login-banned-alert"
                                    role="alert"
                                    style={{
                                        margin: '-4px 0 20px', padding: '15px 16px',
                                        background: '#fff8f8', border: '1px solid #efb8ad',
                                        borderRadius: 12, color: '#8f2f22',
                                    }}
                                >
                                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 5 }}>账号已被封禁</div>
                                    <div style={{ fontSize: 13, lineHeight: 1.65 }}>
                                        {bannedAccount.reason ? `原因：${bannedAccount.reason}` : '该账号目前无法登录。'}
                                    </div>
                                    <div style={{ marginTop: 5, fontSize: 12, lineHeight: 1.6, color: '#9c5a50' }}>
                                        如有疑问，请联系平台客服进行核实。
                                    </div>
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -10, marginBottom: 24 }}>
                                <a href="/forgetPassword" style={{ fontSize: 12, color: C.ink3, textDecoration: 'none' }}
                                    onMouseOver={e => e.target.style.color = C.brand}
                                    onMouseOut={e => e.target.style.color = C.ink3}>
                                    {t('Forget Password')}
                                </a>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    width: '100%', padding: '15px',
                                    background: loading ? C.hair2 : C.ink,
                                    color: '#fff', border: 'none', borderRadius: 12,
                                    fontFamily: C.sans, fontSize: 15, fontWeight: 500,
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    transition: 'background .2s',
                                    letterSpacing: 0.3,
                                }}>
                                {loading ? '登录中…' : t('Sign in')}
                            </button>

                            {/* divider */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
                                <div style={{ flex: 1, height: 1, background: C.hair }} />
                                <span style={{ fontSize: 12, color: C.ink3 }}>或</span>
                                <div style={{ flex: 1, height: 1, background: C.hair }} />
                            </div>

                            {/* WeChat login */}
                            <button
                                data-testid="wechat-login-start"
                                type="button"
                                onClick={handleWxLogin}
                                style={{
                                    width: '100%', padding: '14px',
                                    background: '#fff',
                                    color: C.ink2, border: `1.5px solid ${C.hair}`, borderRadius: 12,
                                    fontFamily: C.sans, fontSize: 14, fontWeight: 500,
                                    cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                                    transition: 'border-color .2s',
                                }}
                                onMouseOver={e => e.currentTarget.style.borderColor = '#07C160'}
                                onMouseOut={e => e.currentTarget.style.borderColor = C.hair}>
                                <img src={wechat_logo} alt="WeChat" width={22} height={18} />
                                {t('Wechat Sign in')}
                            </button>

                            {/* sign up */}
                            <p style={{ textAlign: 'center', marginTop: 28, fontSize: 13, color: C.ink3 }}>
                                还没有账号？{' '}
                                <a href="/signup" style={{ color: C.brand, textDecoration: 'none', fontWeight: 500 }}>
                                    {t('Sign up for free')}
                                </a>
                            </p>
                        </form>
                    )}

                    {/* ── WeChat QR panel ── */}
                    {wxLogin && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{
                                position: 'relative',
                                padding: 12,
                                background: '#fff',
                                borderRadius: 16,
                                border: `1.5px solid ${C.hair}`,
                                boxShadow: '0 8px 32px -8px rgba(0,0,0,.08)',
                            }}>
                                {wxLoginQrCode ? (
                                    <img
                                        data-testid="wechat-login-qr"
                                        width={240} height={240}
                                        src={`data:image/png;base64,${wxLoginQrCode}`}
                                        alt="WeChat Login QR"
                                        style={{ display: 'block', borderRadius: 6 }}
                                    />
                                ) : (
                                    <div data-testid="wechat-login-empty-state" style={{
                                        width: 240, height: 240, borderRadius: 8,
                                        background: C.paper2,
                                        display: 'flex', flexDirection: 'column',
                                        alignItems: 'center', justifyContent: 'center',
                                        gap: 14, color: C.ink2,
                                    }}>
                                        <div style={{
                                            width: 58, height: 58, borderRadius: 29,
                                            background: '#fff', border: `1.5px solid ${C.hair2}`,
                                            display: 'grid', placeItems: 'center',
                                            fontFamily: C.sans, fontSize: 25, color: C.brand,
                                        }}>↻</div>
                                        <div style={{ fontFamily: C.serif, fontSize: 22, color: C.ink }}>
                                            {wxLoginStatus === 'expired'
                                                ? '二维码已失效'
                                                : wxLoginStatus === 'consumed'
                                                    ? '二维码已使用'
                                                    : '暂时无法生成'}
                                        </div>
                                        <div style={{ fontFamily: C.sans, fontSize: 12, color: C.ink3 }}>
                                            重新生成后即可继续登录
                                        </div>
                                    </div>
                                )}
                                {wxLoginQrCode && wxLoginStatus === 'scanned' && (
                                    <div style={{
                                        position: 'absolute', inset: 12,
                                        background: 'rgba(255,255,255,.92)',
                                        borderRadius: 6,
                                        display: 'flex', flexDirection: 'column',
                                        alignItems: 'center', justifyContent: 'center',
                                        gap: 8,
                                    }}>
                                        <div style={{
                                            width: 56, height: 56, borderRadius: 28,
                                            background: '#07C160',
                                            display: 'grid', placeItems: 'center',
                                            fontSize: 28, color: '#fff',
                                        }}>✓</div>
                                        <span style={{ fontFamily: C.serif, fontSize: 18, color: C.ink }}>
                                            已扫码，正在确认账号
                                        </span>
                                    </div>
                                )}
                            </div>

                            {wxLoginError && (
                                <div data-testid="wechat-login-error" style={{ marginTop: 18, fontSize: 13, color: '#c2412e' }}>
                                    {wxLoginError}
                                </div>
                            )}

                            <p style={{ marginTop: 20, fontSize: 13, color: C.ink3, textAlign: 'center', lineHeight: 1.6 }}>
                                {wxLoginError ? '请重新生成二维码后再使用微信扫码' : '打开微信，扫描上方二维码登录'}
                            </p>

                            {wxLoginError && (
                                <button data-testid="wechat-login-retry" onClick={handleWxLogin} style={{
                                    padding: '12px 24px', background: C.ink, color: '#fff', border: 'none',
                                    borderRadius: 10, fontFamily: C.sans, fontSize: 14, cursor: 'pointer',
                                }}>
                                    重新生成二维码
                                </button>
                            )}

                            <button
                                data-testid="wechat-login-back"
                                onClick={goBack}
                                style={{
                                    marginTop: 8, padding: '12px 32px',
                                    background: 'transparent',
                                    color: C.ink3, border: `1.5px solid ${C.hair}`, borderRadius: 10,
                                    fontFamily: C.sans, fontSize: 14, cursor: 'pointer',
                                }}>
                                {t('Go Back')}
                            </button>
                        </div>
                    )}
                </div>

                {/* ICP footer */}
                <div style={{ marginTop: 48, opacity: 0.5 }}>
                    <ICP />
                </div>
            </div>

            <style>{`
                @media (min-width: 720px) {
                    .login-panel { display: flex !important; }
                }
            `}</style>
        </div>
    );
});

export default inject('snackbarStore')(UserLoginPage);
